import { useEffect, useState, ReactNode } from 'react'
import {
  IQuantitySelectorOption,
  QuantitySelector,
} from '@components/common/QuantitySelector'
import { Button } from '@noissue-ui-kit/button'
import { FormInput } from '@noissue-ui-kit/oldBranding/formInput'
import {
  IRadioTileOption,
  RadioTileGroup,
} from '@noissue-ui-kit/oldBranding/radioTile'
import { calculateGridOverrideClass } from 'app/(main)/(catalog)/upload/[slug]/_components/designUpload/components/VariantOptionSelection.utils'
import { useSession } from 'next-auth/react'
import {
  answersToHubspotPayload,
  getInitialSelection,
  getSelectedOption,
  usePlusFormFields,
  trackRequesAQuotetFormInteraction,
  trackRequestAQuoteModalInteraction,
  QUANTITY_OPTION_DIVIDER,
} from './utils/plusForm.utils'
import { trackRequestQuoteAction } from '@utils/gtm'
import {
  TFieldOption,
  TFieldSpecification,
  THubspotFormSpecification,
} from '@utils/form/hubspot/schema'
import { Modal } from '@noissue-ui-kit/modal'
import { useRouter } from 'next/navigation'
import {
  DropdownSelect,
  IDropdownOption,
} from '@noissue-ui-kit/oldBranding/dropdownSelect'
import { useRequestAQuoteSubmission } from './useRequestAQuoteSubmission.hook'
import { PLUS_QUOTE_EVENT_NAME } from '../constants'
import { FormFieldTooltip } from '@noissue-ui-kit/form/form/FormFieldTooltip'
import {
  findDependentFieldsToDisplay,
  formatFieldLabel,
  formatFieldName,
  mergeDependentAndRegularFields,
} from './utils/dependentFields.utils'
import isEqual from 'lodash/isEqual'
import { IProductEntry } from 'app/_services/CMS/contentful/types/productEntry'
import { ContentfulMarkdown } from '@utils/contentful/ContentfulMarkdown'
import {
  canDisplayRegionSpecificUnits,
  getFormattedOptions,
  isSizeField,
} from './utils/formatSizeLabels.utils'
import {
  TUnitsSystem,
  UNIT_SYSTEMS,
  getPreferredUnitsSystem,
} from 'app/(main)/(catalog)/upload/[slug]/_components/designUpload/components/utils/formatOptionLabel'
import { Switch } from '@noissue-ui-kit/switch'

export function ProductPlusForm({
  hubspotPlusForm,
  productData,
}: {
  hubspotPlusForm: THubspotFormSpecification
  productData: IProductEntry
}) {
  const { data: session } = useSession()
  const {
    productName,
    productSlug,
    productType,
    productHeroImages,
    productPlusEditorConfig,
    alternativeDtcPagePath,
    displayAlternativeDtcPath,
    correspondingPlusProductSku,
    customSizeNote,
  } = productData

  const hasPlusEditorConfig = Boolean(productPlusEditorConfig)
  const formFields: TFieldSpecification[] = usePlusFormFields({
    formSpecification: hubspotPlusForm,
  })
  const [dependentFields, setDependentFields] = useState<TFieldSpecification[]>(
    []
  )
  // Will consist of both regular [formFields] and dependent fields
  const [combinedFormFields, setCombinedFormFields] =
    useState<TFieldSpecification[]>(null)

  const [fieldsErrors, setFieldsErrors] = useState<Record<string, string>>({})
  const [plusFormAnswers, setPlusFormAnswers] = useState<
    Record<string, string>
  >(getInitialSelection(formFields) || {})
  const [customQuantity, setCustomQuantity] = useState<number>()

  const router = useRouter()
  const {
    formIsLoading,
    setFormIsLoading,
    submissionError,
    showSuccessDialog,
    setShowSuccessDialog,
    submitForm,
    handleUserLogin,
    preSubmittedPayload,
    savePayloadForPlusEditorFlow,
    saveQuoteForDefaultRAQFlow,
  } = useRequestAQuoteSubmission({
    formEvent: PLUS_QUOTE_EVENT_NAME,
    productSlug,
  })

  const handleSubmit = async () => {
    const payload = answersToHubspotPayload({
      plusFormAnswers,
      productSlug,
      formFields,
      productName,
      productImage: productHeroImages[0]?.fields?.file?.url,
      sku: correspondingPlusProductSku,
    })

    if (!session?.email) {
      const redirectUrl = hasPlusEditorConfig
        ? window?.location?.origin + `/quote/${productSlug}`
        : window?.location?.pathname

      hasPlusEditorConfig
        ? savePayloadForPlusEditorFlow(payload)
        : saveQuoteForDefaultRAQFlow(payload)

      await handleUserLogin(redirectUrl)
      return
    }

    if (hasPlusEditorConfig) {
      setFormIsLoading(true)
      savePayloadForPlusEditorFlow(payload)
      await router.push(`/quote/${productSlug}`)
    } else {
      await submitForm(payload, productSlug)
    }
  }

  useEffect(() => {
    setPlusFormAnswers(getInitialSelection(formFields) || {})
  }, [formFields])

  /** This useEffect rewrites default option selection on dependentFields change.
   * In the first if we are checking if updated fields has the same option, as the previous one, to preserve exact selection if possible. */
  useEffect(() => {
    const newSelection = { ...plusFormAnswers }

    for (const field of dependentFields) {
      const propName = formatFieldName(field.name)

      if (field?.fieldType === 'radio' || field?.fieldType === 'dropdown') {
        newSelection[propName] = field.options?.find(
          (x) => x.value === newSelection[propName]
        )
          ? newSelection[propName]
          : field.options?.[0]?.value
      } else {
        newSelection[propName] = newSelection[propName] || ''
      }
    }

    if (!isEqual(plusFormAnswers, newSelection)) {
      setPlusFormAnswers(newSelection)
    }
  }, [dependentFields])

  useEffect(() => {
    const preloadedFields = JSON.parse(preSubmittedPayload)?.fields

    if (preloadedFields?.length && session?.email) {
      const answers = {}

      for (const field of preloadedFields) {
        answers[field.name] = field?.value
      }
      // prepopulate form with preloaded answers for visibility
      setPlusFormAnswers(answers)
      // submit the form with preloaded payload
      submitForm(JSON.parse(preSubmittedPayload), productSlug)
    }
  }, [session])

  const resetForm = () => {
    setPlusFormAnswers(getInitialSelection(combinedFormFields) || {})
    setCustomQuantity(null)
  }

  const onSelection = ({
    fieldName,
    value,
    label,
    fieldType,
  }: {
    fieldName: string
    value: string
    label: string
    fieldType: string
  }) => {
    const formattedFieldName = formatFieldName(fieldName)

    setPlusFormAnswers({
      ...plusFormAnswers,
      [formattedFieldName]: value,
    })

    trackRequesAQuotetFormInteraction({
      componentName: formattedFieldName,
      componentLabel: label,
      componentValue: value,
      productSlug,
      componentType: fieldType,
    })
  }

  // On selection change, check if we have any dependent field to display and push it to combinedFields array
  useEffect(() => {
    const dependentFields = Object.keys(plusFormAnswers)
      .map((fieldName) => {
        const parentField = combinedFormFields?.find(
          (x) => formatFieldName(x.name) === fieldName
        )

        const dependentFields = findDependentFieldsToDisplay({
          parentField,
          parentFieldValue: plusFormAnswers[fieldName],
        })

        return { fields: dependentFields, parentFieldName: fieldName }
      })
      .filter((x) => x.fields?.length)

    setDependentFields(
      dependentFields
        ?.map((x) => x.fields)
        ?.filter(Boolean)
        ?.flat()
    )

    const mergedFields = mergeDependentAndRegularFields(
      dependentFields,
      formFields
    )

    setCombinedFormFields(mergedFields)
  }, [plusFormAnswers])

  // Override selected quantity on customQuantity change
  useEffect(() => {
    if (customQuantity) {
      const qunatityField = combinedFormFields.find((field) => {
        return field?.name?.toLowerCase().includes('quantity')
      })

      onSelection({
        fieldName: formatFieldName(qunatityField?.name),
        value: String(customQuantity),
        label: qunatityField?.label,
        fieldType: qunatityField?.fieldType,
      })
    }
  }, [customQuantity])

  const renderHubspotField = (field: TFieldSpecification) => {
    const selectedOption = getSelectedOption({
      plusFormAnswers,
      field,
    })

    const options = field?.options?.map((x: TFieldOption) => {
      const quantitySelectorOption: IQuantitySelectorOption = {
        displayLabel: x.label
          ?.toString()
          ?.split(QUANTITY_OPTION_DIVIDER)?.[0]
          ?.trim(),
        indicativePricing:
          x.label?.toString()?.split(QUANTITY_OPTION_DIVIDER)?.[1]?.trim() ||
          null,
        key: x.value?.trim(),
        optionType: 'Plus',
      }
      return quantitySelectorOption
    })

    if (field?.fieldType === 'radio') {
      if (field?.name?.toLowerCase()?.includes('quantity')) {
        return (
          <QuantitySelector
            options={options as IQuantitySelectorOption[]}
            onChange={(option) => {
              if (option) {
                onSelection({
                  fieldName: field?.name,
                  value: option?.key,
                  label: field?.label,
                  fieldType: field?.fieldType,
                })
              }
            }}
            selected={selectedOption as IQuantitySelectorOption}
            className={`mb-10`}
            onError={(error) => {
              setFieldsErrors({
                ...fieldsErrors,
                [field?.name]: error,
              })
            }}
            productType={productType}
            customQuantity={customQuantity}
            setCustomQuantity={setCustomQuantity}
            includeQuoteOption={true}
            alternativeDtcPagePath={
              displayAlternativeDtcPath ? alternativeDtcPagePath : null
            }
            key={field?.name}
          />
        )
      } else {
        const [displayOptions, displaySelectedOption] =
          getFormattedOptions<IRadioTileOption>(
            field,
            options as IRadioTileOption[],
            selectedOption as IRadioTileOption,
            fieldModifiers
          )

        return (
          <RadioTileGroup
            options={displayOptions}
            optionName={field?.label}
            onChange={({ key }) => {
              onSelection({
                fieldName: field?.name,
                value: key,
                label: field?.label,
                fieldType: field?.fieldType,
              })
            }}
            selected={displaySelectedOption || (options[0] as IRadioTileOption)}
            radioGroupClassName={`w-full mb-5 ${calculateGridOverrideClass(
              options
            )}`}
            key={field?.name}
          />
        )
      }
    } else if (field?.fieldType === 'dropdown') {
      const [displayOptions, displaySelectedOption] =
        getFormattedOptions<IDropdownOption>(
          field,
          options as IDropdownOption[],
          selectedOption as IDropdownOption,
          fieldModifiers
        )

      return (
        <DropdownSelect
          options={displayOptions}
          onChange={({ key }) => {
            onSelection({
              fieldName: field?.name,
              value: key,
              label: field?.label,
              fieldType: field?.fieldType,
            })
          }}
          selected={displaySelectedOption as IDropdownOption}
          optionName={field?.label}
          optionsListClassName="relative"
          key={field?.name}
        />
      )
    } else if (field?.fieldType === 'single_line_text') {
      return (
        <FormInput
          name={field?.name}
          type="text"
          placeholder={field?.placeholder}
          onChange={(event) => {
            onSelection({
              fieldName: field?.name,
              value: event?.target?.value,
              label: field?.label,
              fieldType: field?.fieldType,
            })
          }}
          initialValue={plusFormAnswers[field?.name]}
          key={field?.name}
        />
      )
    }
  }

  const preferredMetricSystem: TUnitsSystem = getPreferredUnitsSystem()
  const [fieldModifiers, setFieldModifiers] = useState<Record<string, boolean>>(
    {}
  )

  const renderLabelSwitch = (field) => {
    if (isSizeField(field?.name)) {
      const displayRegionSpecificUnits = canDisplayRegionSpecificUnits(
        field?.options?.map((x) => {
          return {
            ...x,
            key: x?.value,
            displayLabel: x?.label?.toString(),
          }
        })
      )

      return displayRegionSpecificUnits ? (
        <Switch
          id="size-units-switch"
          onLabel={preferredMetricSystem === UNIT_SYSTEMS.METRIC ? 'in' : 'mm'}
          offLabel={preferredMetricSystem === UNIT_SYSTEMS.METRIC ? 'mm' : 'in'}
          className="ml-4 mr-2"
          onChange={(checked) => {
            setFieldModifiers({
              ...fieldModifiers,
              size: checked,
            })
          }}
        />
      ) : null
    }
  }

  return (
    <>
      {combinedFormFields?.map((field) => {
        return (
          !field?.hidden && (
            <>
              <div
                className="flex items-center mb-4 text-xl text-acai"
                aria-label={`select ${field?.label?.toLowerCase()}`}
                key={field.name + 'label'}
              >
                <span className="capitalize">
                  {formatFieldLabel(field?.label)}
                </span>

                {renderLabelSwitch(field)}

                {field?.description && (
                  <FormFieldTooltip>{field.description}</FormFieldTooltip>
                )}

                {Boolean(customSizeNote) && isSizeField(field?.name) && (
                  <FormFieldTooltip
                    snapToRight={true}
                    className="ml-auto"
                    customLabel={
                      <span className="text-acai text-2xl">
                        Need a <span className="underline">custom size?</span>
                      </span>
                    }
                  >
                    <ContentfulMarkdown
                      overrideClass="text-acai text-xl"
                      overrideStyles={{
                        a: ({ children, href }) => (
                          <a
                            className="text-acai text-xl underline"
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {` ${children} `}
                          </a>
                        ),
                      }}
                    >
                      {customSizeNote}
                    </ContentfulMarkdown>
                  </FormFieldTooltip>
                )}
              </div>

              {renderHubspotField(field)}
            </>
          )
        )
      })}

      {submissionError && (
        <p className="mb-8 text-xl text-error font-mori">{submissionError}</p>
      )}

      <Button
        size={'m'}
        corners={'rounded'}
        className="w-full"
        colour="pitaya"
        Component="button"
        onClick={() => {
          trackRequestQuoteAction(productName)
          handleSubmit()
        }}
        loading={formIsLoading}
        disabled={Boolean(Object.values(fieldsErrors)?.filter(Boolean)?.length)}
      >
        {hasPlusEditorConfig
          ? 'Continue'
          : hubspotPlusForm?.displayOptions?.submitButtonText}
      </Button>

      <Modal
        opened={showSuccessDialog}
        size="medium"
        title={'Quote Requested!'}
        confirmLabel={'Shop all'}
        cancelLabel="Request a quote (same product)"
        onConfirm={() => {
          trackRequestAQuoteModalInteraction({
            componentValue: 'Shop all',
            productSlug,
          })
          router.push('/category/all/')
        }}
        onCancel={() => {
          trackRequestAQuoteModalInteraction({
            componentValue: 'Request a quote (same product)',
            productSlug,
          })
          setShowSuccessDialog(false)
          resetForm()
        }}
        showControls={{
          cancelIcon: true,
          cancelButton: true,
          confirmButton: true,
        }}
        confirmButtonDisabled={false}
      >
        <p className="text-2xl text-acai">
          Nice one! An Account Manager will be in touch with your quote within
          24-48 hours, via email.
        </p>
      </Modal>
    </>
  )
}
